import { PLANS } from "$lib/data/pricing";

declare global {
  interface Window {
    gtag: (
      command: "event",
      action: string,
      params: {
        send_to: string;
        value?: number;
        currency?: string;
        transaction_id?: string;
        plan?: string;
        [key: string]: any;
      },
    ) => void;
  }
}

/**
 * Find the price for a given plan label
 * @param planLabel - The selected plan label
 * @returns The price of the plan or 0 if not found
 */
function getPlanPrice(planLabel: string): number {
  if (!planLabel) return 0;

  if (planLabel === "free_monthly_tier_1") return 0;

  let planType: "FREE" | "PLUS" | "TEAM" = "FREE";

  if (planLabel.startsWith("plus_tier")) {
    planType = "PLUS";
  } else if (planLabel.startsWith("team_tier")) {
    planType = "TEAM";
  }

  const plan = PLANS[planType];
  if ("subtiers" in plan) {
    const matchingTier = plan.subtiers.find((tier) => tier.label === planLabel);
    return matchingTier?.price || 0;
  }
  return plan.price || 0;
}

/**
 * Track a subscription upgrade conversion in Google Ads
 * @param planLabel - The plan the user upgraded to
 * @param customValue - Optional override for the plan price
 * @param transaction_id - Optional unique transaction ID
 */
export function trackUpgradeConversion(planLabel: string, customValue?: number, transaction_id?: string): void {
  if (typeof window === "undefined") {
    logger.warn("DataLayer tracking attempted but window not available");
    return;
  }

  const value = customValue !== undefined ? customValue : getPlanPrice(planLabel);
  const txnId = transaction_id || generateTransactionId();

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "subscription_update",
    ecommerce: {
      purchase: {
        transaction_id: txnId,
        value: value,
        currency: "USD",
        items: [
          {
            item_name: `${getPlanTier(planLabel)} plan`,
            item_id: planLabel,
            price: value,
          },
        ],
      },
    },
    plan: planLabel,
    plan_tier: getPlanTier(planLabel),
    billing_period: planLabel.includes("_annual") ? "annual" : "monthly",
  });

  logger.log(`Pushed subscription update to dataLayer: ${planLabel} with value: $${value}`);
}

/**
 * Extract the tier information from the plan label
 */
function getPlanTier(planLabel: string): string {
  if (!planLabel) return "";

  if (planLabel === "free_monthly_tier_1") return "free";

  if (planLabel.startsWith("plus_tier")) {
    return "plus";
  } else if (planLabel.startsWith("team_tier")) {
    return "team";
  }

  return "";
}

/**
 * Generate a unique transaction ID if one isn't provided
 */
function generateTransactionId(): string {
  return `txn_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Track the beginning of checkout for subscription
 * Used for tracking conversions that happen via Stripe checkout
 */
export function trackBeginCheckout(planLabel: string): void {
  if (typeof window === "undefined") {
    logger.warn("DataLayer tracking attempted but window not available");
    return;
  }

  const value = getPlanPrice(planLabel);
  const checkoutId = generateTransactionId();

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "begin_checkout",
    ecommerce: {
      purchase: {
        transaction_id: checkoutId,
        value: value,
        currency: "USD",
        items: [
          {
            item_id: planLabel,
            item_name: `${getPlanTier(planLabel)} plan`,
            price: value,
          },
        ],
      },
    },
    plan: planLabel,
    plan_tier: getPlanTier(planLabel),
    billing_period: planLabel.includes("_annual") ? "annual" : "monthly",
  });

  logger.log(`Pushed new subscription to dataLayer for: ${planLabel} with value: $${value}`);
}
